/**
 * @fileoverview Sidebar navigation component rendering navigation items with
 * support for headers, dividers, and nested item expansion.
 */

import * as React from 'react';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListSubheader from '@mui/material/ListSubheader';
import type {} from '@mui/material/themeCssVarsAugmentation';
import type { Navigation, NavigationPageItem } from '../../types';
import { useActivePage } from '../../hooks/useActivePage';
import { NavigationContext } from '../../contexts';
import { getItemPath, getItemTitle, hasSelectedNavigationChildren, isPageItem } from '../../utils/navigationHelpers';
import { SidebarPageItemContext } from './context';
import { SidebarExpansionContext } from './SidebarExpansionContext';
import { SidebarPageItem, SidebarPageItemContextProps } from './SidebarPageItem';
import { getDrawerSxTransitionMixin } from './utils';

interface SidebarNavListItemProps {
  id: string;
  item: NavigationPageItem;
  isExpanded: boolean;
  onClick: (itemId: string, item: NavigationPageItem) => void;
  depth: number;
  onLinkClick: () => void;
  isFullyExpanded: boolean;
  sidebarExpandedWidth: number | string;
}

/**
 * @ignore - internal component.
 */
function SidebarNavListItem({
  id,
  item,
  isExpanded,
  onClick,
  depth,
  onLinkClick,
  isFullyExpanded,
  sidebarExpandedWidth,
}: SidebarNavListItemProps): React.JSX.Element {
  const navigationContext = React.useContext(NavigationContext);

  const activePage = useActivePage();

  const isActive = !!activePage && activePage.path === getItemPath(navigationContext, item);

  const isSelected = isActive && !item.children;

  const pageItemContextProps: SidebarPageItemContextProps = React.useMemo(
    () => ({
      expanded: isExpanded,
      selected: isSelected,
      id,
      onClick,
      isSidebarFullyExpanded: isFullyExpanded,
      renderNestedNavigation: () => (
        <SidebarNavList
          subNavigation={item.children ?? []}
          depth={depth + 1}
          onLinkClick={onLinkClick}
          sidebarExpandedWidth={sidebarExpandedWidth}
        />
      ),
    }),
    [
      depth,
      id,
      isExpanded,
      isFullyExpanded,
      isSelected,
      item.children,
      onClick,
      onLinkClick,
      sidebarExpandedWidth,
    ],
  );

  return (
    <SidebarPageItemContext.Provider value={pageItemContextProps}>
      <SidebarPageItem item={item} />
    </SidebarPageItemContext.Provider>
  );
}

interface SidebarNavListProps {
  subNavigation: Navigation;
  depth?: number;
  onLinkClick: () => void;
  isFullyExpanded?: boolean;
  hasDrawerTransitions?: boolean;
  sidebarExpandedWidth: number | string;
}

/**
 * @ignore - internal component.
 */
function SidebarNavList({
  subNavigation,
  depth = 0,
  onLinkClick,
  isFullyExpanded = true,
  hasDrawerTransitions = false,
  sidebarExpandedWidth,
}: SidebarNavListProps): React.JSX.Element {
  const navigationContext = React.useContext(NavigationContext);

  const activePage = useActivePage();

  const initialExpandedItemIds = React.useMemo(
    () =>
      subNavigation
        .map((navigationItem, navigationItemIndex) => ({
          navigationItem,
          originalIndex: navigationItemIndex,
        }))
        .filter(
          ({ navigationItem }) =>
            isPageItem(navigationItem) &&
            !!activePage &&
            hasSelectedNavigationChildren(navigationContext, navigationItem, activePage.path),
        )
        .map(({ originalIndex }) => `page-${depth}-${originalIndex}`),
    [activePage, depth, navigationContext, subNavigation],
  );

  // Depth-0 expansion state is lifted into a shared context so the app bar's
  // "Collapse all" button can drive it. Nested lists keep their own local state.
  const sidebarExpansion = React.useContext(SidebarExpansionContext);
  const useSharedExpansion = depth === 0 && sidebarExpansion !== null;

  const [localExpandedItemIds, setLocalExpandedItemIds] = React.useState(initialExpandedItemIds);

  const expandedItemIds = useSharedExpansion
    ? sidebarExpansion.expandedItemIds
    : localExpandedItemIds;
  const setExpandedItemIds = useSharedExpansion
    ? sidebarExpansion.setExpandedItemIds
    : setLocalExpandedItemIds;

  // Sync expanded items when navigation changes (e.g., when project children load dynamically)
  React.useEffect(() => {
    setExpandedItemIds((prev) => {
      // Merge new items that should be expanded with current expanded items
      const newIds = initialExpandedItemIds.filter((id) => !prev.includes(id));
      return newIds.length > 0 ? [...prev, ...newIds] : prev;
    });
  }, [initialExpandedItemIds, setExpandedItemIds]);

  const handlePageItemClick = React.useCallback(
    (itemId: string, item: NavigationPageItem) => {
      if (item.children) {
        setExpandedItemIds((previousValue) =>
          previousValue.includes(itemId)
            ? previousValue.filter((previousValueItemId) => previousValueItemId !== itemId)
            : [...previousValue, itemId],
        );
      } else {
        onLinkClick();
      }
    },
    [onLinkClick, setExpandedItemIds],
  );

  return (
    <List
      sx={{
        padding: 0,
        mb: depth === 0 ? 4 : 0.5,
        pl: 2 * depth,
      }}
    >
      {subNavigation.map((navigationItem, navigationItemIndex) => {
        if (navigationItem.kind === 'header') {
          return (
            <ListSubheader
              key={`subheader-${depth}-${navigationItemIndex}`}
              sx={{
                fontSize: '0.6875rem',
                fontWeight: '700',
                height: 37,
                ...(hasDrawerTransitions
                  ? getDrawerSxTransitionMixin(isFullyExpanded, 'height')
                  : {}),
                px: 1.5,
                minWidth: sidebarExpandedWidth,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                zIndex: 2,
              }}
            >
              {getItemTitle(navigationItem)}
            </ListSubheader>
          );
        }

        if (navigationItem.kind === 'divider') {
          const nextItem = subNavigation[navigationItemIndex + 1];

          return (
            <li key={`divider-${depth}-${navigationItemIndex}`}>
              <Divider
                sx={{
                  borderBottomWidth: 2,
                  mx: 1,
                  mt: 1,
                  mb: nextItem?.kind === 'header' ? 0 : 1,
                  ...(hasDrawerTransitions
                    ? getDrawerSxTransitionMixin(isFullyExpanded, 'margin')
                    : {}),
                }}
              />
            </li>
          );
        }

        const pageItemId = `page-${depth}-${navigationItemIndex}`;

        return (
          <SidebarNavListItem
            key={pageItemId}
            id={pageItemId}
            item={navigationItem}
            isExpanded={expandedItemIds.includes(pageItemId)}
            onClick={handlePageItemClick}
            depth={depth}
            onLinkClick={onLinkClick}
            isFullyExpanded={isFullyExpanded}
            sidebarExpandedWidth={sidebarExpandedWidth}
          />
        );
      })}
    </List>
  );
}

export { SidebarNavList };
